import React, { FC, memo, useCallback, useEffect, useRef } from 'react';
import { LoadMoreDirection, SettingsScreens } from '../../../types';
import { useTranslation } from 'react-i18next';
import MenuItem from '../../ui/MenuItem';
import ChatFeedList from './ChatFeedList';
import { GlobalState, ISearchQuery } from '../../../global/types';
import useLastCallback from '../../../hooks/useLastCallback';
import { getActions, withGlobal } from '../../../global';
import { selectTabState } from '../../../global/selectors';
import CountryModal from '../../common/CountryModal';
import useFlag from '../../../hooks/useFlag';
import { ApiCountryCode } from '../../../api/types';
import CategoryModal from '../../common/CategoryModal';
import useEffectOnce from '../../../hooks/useEffectOnce';
import { FeedLeftList, FeedMiddleList } from '../../main/Main';
import { throttle } from '../../../util/schedulers';
import resetScroll from '../../../util/resetScroll';
import classNames from 'classnames';
import './FeedChats.scss';
import renderText from '../../common/helpers/renderText';
import useConnectionStatus, {
  ConnectionStatus,
} from '../../../hooks/useConnectionStatus';

const runThrottled = throttle((cb) => cb(), 500, true);

export type OwnProps = {
  isActive: boolean;
  feedLeftScreen: FeedLeftList;
  setFeedLeftScreen: (screen: FeedLeftList) => void;
  feedMiddleScreen: FeedMiddleList;
  setFeedMiddleScreen: (screen: FeedMiddleList) => void;
};

type StateProps = {
  filter?: ISearchQuery;
  query?: string;
  phoneCodeList: ApiCountryCode[];
  emptyChats?: boolean;
  lastSyncTime?: number;
} & Pick<GlobalState, 'connectionState' | 'isSyncing'>;

const FeedChats: FC<OwnProps & StateProps> = ({
  isActive,
  filter,
  query,
  lastSyncTime,
  phoneCodeList,
  emptyChats,
  connectionState,
  isSyncing,
  feedLeftScreen,
  feedMiddleScreen,
  setFeedLeftScreen,
  setFeedMiddleScreen,
}) => {
  const { searchChannelsGlobal } = getActions();

  // Добавляем ref для отслеживания монтирования
  const isMountedRef = useRef(false);
  const isInitializedRef = useRef(false);

  const [showCountryModal, openCountryModal, closeCountryModal] = useFlag();
  const [showCategoryModal, openCategoryModal, closeCategoryModal] = useFlag();
  const [showGenreModal, openGenreModal, closeGenreModal] = useFlag();

  const slideRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { connectionStatus } = useConnectionStatus(
    t,
    connectionState,
    isSyncing,
    true
  );

  let { isNew, isCourse, isPaid, isPublic, category, country, genre, page } =
    filter || {};

  // Безопасная обертка для searchChannelsGlobal
  const safeSearchChannelsGlobal = useCallback(
    (params: any) => {
      if (isMountedRef.current) {
        try {
          searchChannelsGlobal(params);
        } catch (error) {
          console.error('Error in searchChannelsGlobal:', error);
        }
      }
    },
    [searchChannelsGlobal]
  );

  const handleClickRecomended = useLastCallback(() => {
    setFeedLeftScreen(FeedLeftList.ForYou);
  });

  const handleLoadMoreRecomended = useCallback(
    ({
      direction,
      noScroll,
    }: {
      direction: LoadMoreDirection;
      noScroll: boolean;
    }) => {
      if (
        lastSyncTime &&
        direction === LoadMoreDirection.Backwards &&
        feedLeftScreen === FeedLeftList.ForYou &&
        !noScroll &&
        isMountedRef.current
      ) {
        runThrottled(() => {
          if (page && isMountedRef.current) {
            safeSearchChannelsGlobal({ filter: { page: page + 1 } });
          }
        });
      }
    },
    [feedLeftScreen, lastSyncTime, page, safeSearchChannelsGlobal]
  );

  const handleClickFollowing = useLastCallback(() => {
    setFeedLeftScreen(FeedLeftList.Following);
  });

  const handleClickExplore = useLastCallback(() => {
    setFeedLeftScreen(FeedLeftList.Explore);
  });

  const scrollContainer = containerRef.current!;

  const handleChangeAll = useLastCallback(() => {
    if (scrollContainer) {
      resetScroll(scrollContainer, 0);
    }
    safeSearchChannelsGlobal({ clean: true, filter: { page: 1 } });
  });

  const handleChangeNew = useLastCallback(() => {
    if (scrollContainer) {
      resetScroll(scrollContainer, 0);
    }
    safeSearchChannelsGlobal({
      filter: { ...(isNew ? { isNew: undefined } : { isNew: true }), page: 1 },
    });
  });

  const handleChangeCourse = useLastCallback(() => {
    if (scrollContainer) {
      resetScroll(scrollContainer, 0);
    }
    safeSearchChannelsGlobal({
      filter: {
        ...(isCourse
          ? { isCourse: undefined }
          : { isCourse: true, isPaid: undefined, isPublic: undefined }),
        page: 1,
      },
    });
  });

  const handleChangePaid = useLastCallback(() => {
    if (scrollContainer) {
      resetScroll(scrollContainer, 0);
    }
    safeSearchChannelsGlobal({
      filter: {
        ...(isPaid
          ? { isPaid: undefined }
          : { isPaid: true, isCourse: undefined, isPublic: undefined }),
        page: 1,
      },
    });
  });

  const handleChangeFree = useLastCallback(() => {
    if (scrollContainer) {
      resetScroll(scrollContainer, 0);
    }
    safeSearchChannelsGlobal({
      filter: {
        ...(isPublic
          ? { isPublic: undefined }
          : { isCourse: undefined, isPaid: undefined, isPublic: true }),
        page: 1,
      },
    });
  });

  const handleChangeCountry = useLastCallback(
    (country: ApiCountryCode | undefined) => {
      if (scrollContainer) {
        resetScroll(scrollContainer, 0);
      }
      safeSearchChannelsGlobal({
        filter: { country: country?.countryCode || undefined, page: 1 },
      });
    }
  );

  const handleChangeCategory = useLastCallback(
    (category: string | undefined) => {
      if (scrollContainer) {
        resetScroll(scrollContainer, 0);
      }
      safeSearchChannelsGlobal({
        filter: { genre: undefined, category: category || undefined, page: 1 },
      });
    }
  );

  const handleChangeGenre = useLastCallback((genre: string | undefined) => {
    if (scrollContainer) {
      resetScroll(scrollContainer, 0);
    }
    safeSearchChannelsGlobal({
      filter: { genre: genre || undefined, page: 1 },
    });
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffectOnce(() => {
    if (emptyChats && isMountedRef.current) {
      setFeedLeftScreen(FeedLeftList.ForYou);
    }
  });

  useEffect(() => {
    if (!isMountedRef.current) {
      return;
    }

    if (feedLeftScreen === FeedLeftList.ForYou) {
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current && !isInitializedRef.current) {
          isInitializedRef.current = true;
          safeSearchChannelsGlobal({
            ...(Boolean(filter)
              ? { filter: { ...filter, page: 1 } }
              : { clean: true }),
          });
        }
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      isInitializedRef.current = false;
    }
  }, [feedLeftScreen, filter, safeSearchChannelsGlobal]);

  const currentCountry = phoneCodeList.find((el) => el.countryCode === country);
  const countryName =
    currentCountry && (currentCountry.defaultName || currentCountry.name);

  const container = slideRef.current;

  const slideFilter = useCallback(() => {
    let pageX = 0;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (pageX !== 0 && container) {
        container.scrollLeft = container.scrollLeft + (pageX - e.pageX);
      }
      pageX = e.pageX;
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    if (container) {
      container.ondragstart = () => false;
    }
  }, [container]);

  return (
    <>
      <div className='FeedChats'>
        <div className='LeftMainMenu'>
          <MenuItem
            className={
              feedLeftScreen === FeedLeftList.Following ? 'active' : ''
            }
            onClick={handleClickFollowing}
          >
            {t('Feed.Following')}
          </MenuItem>
          <MenuItem
            className={feedLeftScreen === FeedLeftList.Explore ? 'active' : ''}
            onClick={handleClickExplore}
          >
            {t('Feed.Explore')}
          </MenuItem>
          <MenuItem
            className={feedLeftScreen === FeedLeftList.ForYou ? 'active' : ''}
            onClick={handleClickRecomended}
          >
            {t('Feed.ForYou')}
          </MenuItem>
        </div>
        {feedLeftScreen === FeedLeftList.ForYou && (
          <div
            ref={slideRef}
            onMouseDown={slideFilter}
            className='filter-group custom-scroll'
          >
            <button
              type='button'
              onClick={handleChangeAll}
              className={classNames('Button', {
                selected: filter?.q === '' && Object.keys(filter).length === 2,
              })}
            >
              All
            </button>
            <button
              type='button'
              onClick={handleChangeNew}
              className={classNames('Button', {
                selected: isNew,
              })}
            >
              New
            </button>
            <button
              type='button'
              onClick={handleChangeCourse}
              className={classNames('Button', {
                selected: isCourse,
              })}
            >
              Masterclass
            </button>
            <button
              type='button'
              onClick={handleChangePaid}
              className={classNames('Button', {
                selected: isPaid,
              })}
            >
              Paid
            </button>
            <button
              type='button'
              onClick={handleChangeFree}
              className={classNames('Button', {
                selected: isPublic,
              })}
            >
              Free
            </button>
            <button type='button' onClick={openCountryModal} className='Button'>
              {countryName || 'All country'}
              <i className='css-icon-down' />
            </button>
            <button
              type='button'
              onClick={openCategoryModal}
              className='Button'
            >
              {category || 'All Categories'}
              <i className='css-icon-down' />
            </button>
            {category?.toLowerCase() === 'music' && (
              <button type='button' onClick={openGenreModal} className='Button'>
                {genre || 'All Genres'}
                <i className='css-icon-down' />
              </button>
            )}
          </div>
        )}

        <div
          className={classNames('empty-root', {
            'd-none':
              connectionStatus === ConnectionStatus.online ||
              connectionStatus === ConnectionStatus.syncing,
          })}
        >
          <div className='sticker-wrap'>
            <NoInternetImg />
            <h3>Ooops!</h3>
            <p className='description' dir='auto'>
              {renderText(t('ChatList.NoInternet'), ['br'])}
            </p>
          </div>
        </div>

        {connectionStatus === ConnectionStatus.online && (
          <ChatFeedList
            elRef={containerRef}
            isActive={isActive}
            isFilter={Boolean(filter)}
            feedLeftScreen={feedLeftScreen}
            feedMiddleScreen={feedMiddleScreen}
            setFeedLeftScreen={setFeedLeftScreen}
            setFeedMiddleScreen={setFeedMiddleScreen}
            handleLoadMoreRecomended={handleLoadMoreRecomended}
          />
        )}
      </div>
      <CountryModal
        isOpen={showCountryModal}
        onClose={closeCountryModal}
        confirmHandler={handleChangeCountry}
        value={country}
      />
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={closeCategoryModal}
        confirmHandler={handleChangeCategory}
        value={country}
        title='Category'
      />
      <CategoryModal
        isOpen={showGenreModal}
        isGanre
        onClose={closeGenreModal}
        confirmHandler={handleChangeGenre}
        value={country}
        title='Genre'
      />
    </>
  );
};

export default memo(
  withGlobal<OwnProps>((global): StateProps => {
    const {
      countryList: { phoneCodes: phoneCodeList },
      chats,
      lastSyncTime,
      connectionState,
      isSyncing,
      isSynced,
    } = global;
    const { query, filter } = selectTabState(global).globalSearch;
    return {
      filter,
      query,
      phoneCodeList,
      emptyChats: !chats.listIds.feed,
      lastSyncTime,
      connectionState,
      isSyncing: isSyncing && !isSynced,
    };
  })(FeedChats)
);

const NoInternetImg = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='200'
      height='200'
      fill='none'
    >
      <g clipPath='url(#a)'>
        <ellipse
          cx='144.85'
          cy='156.545'
          fill='#070708'
          fillOpacity='.1'
          rx='55.128'
          ry='10.983'
        />
        <path
          fill='#BF203F'
          d='M200.005 107.88c0 32.944-23.904 59.651-57.928 59.651-47.376 0-63.958-26.707-63.958-59.651s22.181-57.29 63.958-57.29c28.856 0 57.928 24.346 57.928 57.29Z'
        />
        <circle
          cx='130.401'
          cy='109.53'
          r='56.041'
          fill='#EF4062'
          stroke='#D63252'
          strokeWidth='3.736'
        />
        <path
          fill='#fff'
          fillRule='evenodd'
          d='M164.212 79.6a3.446 3.446 0 0 0-4.71-5.03l-10.079 9.437a49.566 49.566 0 0 0-54.31 10.622 51.18 51.18 0 0 0-2.594 2.794 3.543 3.543 0 1 0 5.377 4.616 42.486 42.486 0 0 1 45.776-12.647l-6.06 5.674a35.406 35.406 0 0 0-32.486 9.577 35.942 35.942 0 0 0-2.628 2.929 3.547 3.547 0 0 0-.756 2.603 3.543 3.543 0 0 0 3.909 3.129 3.54 3.54 0 0 0 2.374-1.306 28.33 28.33 0 0 1 22.866-10.637l-9.655 9.04a21.232 21.232 0 0 0-6.096 4.256 21.147 21.147 0 0 0-2.704 3.304 3.562 3.562 0 0 0-.492 1.141l-13.385 12.534a3.445 3.445 0 0 0 4.71 5.03l60.943-57.067Zm-7.282 17.079 5.206-4.875a49.462 49.462 0 0 1 3.073 2.825 50.915 50.915 0 0 1 2.594 2.794 3.538 3.538 0 0 1-.346 5.032c-.357.305-.77.537-1.216.682a3.554 3.554 0 0 1-2.725-.228 3.537 3.537 0 0 1-1.085-.875 42.57 42.57 0 0 0-5.501-5.355Zm-5.232 4.898-5.257 4.924a28.322 28.322 0 0 1 5.846 5.497 3.541 3.541 0 0 0 4.981.551 3.545 3.545 0 0 0 .551-4.982 35.672 35.672 0 0 0-6.121-5.99Zm-10.576 9.904-5.483 5.134a14.16 14.16 0 0 1 6.338 5.252 3.545 3.545 0 0 0 4.907 1.001 3.54 3.54 0 0 0 1.002-4.907 21.276 21.276 0 0 0-2.704-3.304 21.261 21.261 0 0 0-4.06-3.176Zm-11.944 11.184-5.95 5.572a7.08 7.08 0 1 0 5.95-5.572Z'
          clipRule='evenodd'
        />
        <path
          stroke='#FD5273'
          strokeLinecap='round'
          strokeWidth='6.28'
          d='M178.498 109.763c0 26.695-21.641 48.335-48.336 48.335-26.694 0-48.335-21.64-48.335-48.335s21.641-48.335 48.335-48.335'
        />
        <path
          stroke='#EF4062'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='4.307'
          d='M124.58 41.108c-.403-.907-2.49-8.205-3.483-11.74l-5.694 4.02-2.801-10.83M135.096 37.86l2.598-11.956-6.673.95 2.113-10.905M156.135 21.688l-7.347 7.838 5.176 3.24-8.305 8.851'
        />
        <ellipse
          cx='44.922'
          cy='173.153'
          fill='#070708'
          fillOpacity='.1'
          rx='44.421'
          ry='10.036'
        />
        <path
          fill='#2B495F'
          d='M103.041 94.835c3.781 12.055-8.199 24.031-14.44 27.9l-6.452 3.971-11.85-50.66c.192-.87 2.186-2.62 8.616-2.666 6.43-.045 10.925 7.351 12.368 11.055 2.612-.427 8.622 1.055 11.758 10.4Z'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.372'
          d='M83.398 125.961c7.444-3.226 22.246-18.021 20.257-28.96-1.989-10.941-8.961-13.288-12.443-13.055'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.372'
          d='M91.964 90.108c.093-3.535-1.481-11.526-8.52-15.204-7.038-3.677-11.366-.824-12.65 1.062'
        />
        <path
          fill='#2B495F'
          d='M35.068 169.305c-20.117-8.163-16.686-33.597-12.456-45.294 24.816-50.77 52.33-26.463 62.986-7.964 3.212 7.134 7.709 25.086 0 39.819-9.635 18.417-37.367 18.417-50.53 13.439Z'
        />
        <path
          stroke='#1B2E3B'
          strokeWidth='2.372'
          d='M35.068 169.305c-20.117-8.163-16.686-33.597-12.456-45.294 24.816-50.77 52.33-26.463 62.986-7.964 3.212 7.134 7.709 25.086 0 39.819-9.635 18.417-37.367 18.417-50.53 13.439Z'
        />
        <path
          fill='#fff'
          d='M86.455 145.131c-1.87 18.663-7.148 21.632-25.992 24.517-14.355 0-29.674-1.649-29.674-24.517 0-12.972 4.549-30.08 29.674-30.08 22.31 0 28.158 8.447 25.992 30.08Z'
        />
        <path
          stroke='#3E637D'
          strokeLinecap='round'
          strokeWidth='2.139'
          d='M32.925 115.098c-5.205 5.832-12.502 16.781-8.088 38.827'
        />
        <path
          fill='#2B495F'
          d='M86.624 138.273c-7.475-5.682-17.62 3.226-18.364 11.416l-.496 9.431c0 3.473.992 5.971 1.737 8.436 1.736 4.484 5.543 7.214 12.458 7.775 6.915.561 12.148-2.243 12.148-7.663 0-3.319-2.7-5.462-5.001-6.565 1.667-2.646 2.234-8.684 2.234-13.895 0-4.782-3.594-8.355-4.716-8.935Z'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.139'
          d='M83.396 163.095c7.445 0 8.19-6.642 8.19-11.912 0-9.927-1.986-14.64-11.416-14.642-4.715 0-9.115 4.261-10.423 8.933'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.139'
          d='M68.998 162.59c-1.639 1.873-1.898 4.507.559 8.438 2.457 3.932 16.998 5.991 21.502 2.247 2.408-2.072 5.239-7.308-2.703-11.675'
        />
        <path
          fill='#2B495F'
          d='M52.2 176.45c-10.616-4.336-7.788-21.369-5.047-29.343 5.234-13.831 18.128-12.522 21.679-2.43 2.84 8.074.312 15.326-1.308 17.942 1.744.125 5.495 1.383 6.541 5.42 2.243 8.971-8.596 13.831-21.866 8.411Z'
        />
        <path
          fill='#2B495F'
          d='M31.5 162.503c2.32 4.796 6.757 6.438 10.874 6.5 12.718 0 29.25-14.011 25.616-25.033-3.633-11.022-18.603-9.157-25.615-2.967-7.95 7.019-14.502 13.999-10.874 21.5Z'
        />
        <path
          stroke='#3E637D'
          strokeLinecap='round'
          strokeWidth='2.139'
          d='M49.998 148.451c-1.425 3.894-3.652 13.458-1.151 20.565'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.139'
          d='M61.54 162.618c2.18 1.059 6.84 1.981 8.036-2.804 1.495-5.98 1.682-23.922-12.336-23.548-3.355.006-11.009 1.012-14.781 4.982'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.139'
          d='M68.266 162.243c3.738 1.62 9.681 6.616 3.55 13.644-4.859 4.859-23.735 3.738-26.352-7.289-2.093-8.822.25-17.755 1.683-21.119'
        />
        <path
          fill='#2B495F'
          stroke='#1B2E3B'
          strokeWidth='2.372'
          d='M36.207 81.177c-3.127 6.467-9.527 9.279-14.069 7.086-4.54-2.193-6.315-8.952-3.189-15.419 3.127-6.467 9.527-9.279 14.068-7.086 4.542 2.193 6.316 8.951 3.19 15.419Z'
        />
        <ellipse
          cx='5.062'
          cy='6.433'
          fill='#1B2E3B'
          rx='5.062'
          ry='6.433'
          transform='rotate(25.773 -137.305 94.825) skewX(-.026)'
        />
        <ellipse
          cx='.906'
          cy='1.848'
          fill='#5F91B7'
          rx='.906'
          ry='1.848'
          transform='rotate(24.234 -159.121 97.677) skewX(-.025)'
        />
        <path
          fill='#2B495F'
          stroke='#1B2E3B'
          strokeWidth='2.372'
          d='M74.742 77.336c-5.444-4.69-6.507-11.597-3.214-15.414 3.293-3.817 10.285-3.783 15.728.907 5.444 4.69 6.507 11.597 3.214 15.414-3.293 3.817-10.285 3.783-15.728-.907Z'
        />
        <ellipse
          cx='5.062'
          cy='6.434'
          fill='#1B2E3B'
          rx='5.062'
          ry='6.434'
          transform='rotate(140.261 31.817 52.727) skewX(.033)'
        />
        <ellipse
          cx='.906'
          cy='1.848'
          fill='#5F91B7'
          rx='.906'
          ry='1.848'
          transform='rotate(142.824 31.494 49.71) skewX(.032)'
        />
        <path
          fill='#fff'
          stroke='#1B2E3B'
          strokeWidth='2.372'
          d='M99.013 94.295c0 18.326-16.841 33.423-37.93 33.423-21.087 0-37.928-15.097-37.928-33.423 0-18.327 16.84-33.423 37.929-33.423 21.088 0 37.929 15.096 37.929 33.423Z'
        />
        <path
          fill='#1B2E3B'
          d='M67.513 111.959c0 1.973-2.115 5.062-4.23 5.062-1.668 0-2.718-1.302-2.718-3.276 0-1.973.604-4.169 3.927-4.169 1.668 0 3.021.409 3.021 2.383Z'
        />
        <ellipse
          cx='9.863'
          cy='5.112'
          fill='#DFE9F1'
          rx='9.863'
          ry='5.112'
          transform='rotate(-109.033 63.982 47.232) skewX(-.021)'
        />
        <path
          fill='#1B2E3B'
          d='M61.815 98.942c-.516 3.495 5.695 4.271 5.866 4.483 8.334-1.584 7.845-6.55 4.52-7.648-3.325-1.097-9.87-.33-10.386 3.165Z'
        />
        <path
          stroke='#fff'
          strokeLinecap='round'
          strokeWidth='2.415'
          d='M68.414 97.625c1.123-.273 2.733-.068 3.563.44'
        />
        <ellipse
          cx='8.778'
          cy='12.487'
          fill='#39627F'
          rx='8.778'
          ry='12.487'
          transform='rotate(23.758 -163.784 150.293) skewX(-.013)'
        />
        <ellipse
          cx='3.589'
          cy='3.945'
          fill='#1B2E3B'
          rx='3.589'
          ry='3.945'
          transform='rotate(5.329 -965.554 587.443) skewX(.01)'
        />
        <ellipse
          cx='1.614'
          cy='1.973'
          fill='#fff'
          rx='1.614'
          ry='1.973'
          transform='matrix(1 0 .00062 1 53.13 95.186)'
        />
        <ellipse
          cx='6.754'
          cy='11.769'
          fill='#39627F'
          rx='6.754'
          ry='11.769'
          transform='rotate(-26.039 214.174 -107.794) skewX(.035)'
        />
        <ellipse
          cx='3.589'
          cy='3.945'
          fill='#1B2E3B'
          rx='3.589'
          ry='3.945'
          transform='rotate(5.329 -934.445 911.981) skewX(.01)'
        />
        <ellipse
          cx='1.614'
          cy='1.973'
          fill='#fff'
          rx='1.614'
          ry='1.973'
          transform='matrix(1 0 .00058 1 83.324 93.77)'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.139'
          d='M42.842 88.224c1.272-2.132 3.804-4.962 7.865-6.433M83.864 84.483c-.634-1.424-5.431-5.18-9.154-3.692'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.372'
          d='M55.586 61.342c2.33-.438 3.48-1.647 2.852-3.828'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.372'
          d='M56.333 61.236c3.863 1.38 9.418-2.694 5.848-7.514'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeWidth='2.372'
          d='M56.487 61.382c5.422 3.464 9.744-1.199 10.26-4.947'
        />
        <path
          fill='#2B495F'
          d='M11.421 111.071c-1.19 9.926 14.063 20.185 21.59 20.351 1.488 0 6.205 0 9.431-3.723 4.964-5.727-2.235-18.612-7.445-19.853 2.813-2.151 6.056-6.104 7.445-11.663 1.737-11.169-4.961-16.63-12.406-15.389-5.956.993-7.114 8.852-6.949 12.657-3.392 1.737-10.474 7.693-11.666 17.62Z'
        />
        <path
          stroke='#3E637D'
          strokeLinecap='round'
          strokeWidth='2.139'
          d='M15.15 116.035c-1.903-4.384-2.383-14.443 10.919-19.605'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2.372'
          d='M31.773 131.175c-8.19-.083-22.98-10.523-20.598-22.832 1.682-8.686 9.677-14.145 16.378-15.386'
        />
        <path
          stroke='#1B2E3B'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2.372'
          d='M22.342 93.947c.082-4.467 2.035-13.45 9.182-13.65 8.934-.248 13.65 9.433 10.671 16.63-2.212 5.349-4.467 7.446-7.197 10.92'
        />
      </g>
      <defs>
        <clipPath id='a'>
          <path fill='#fff' d='M0 0h200v200H0z' />
        </clipPath>
      </defs>
    </svg>
  );
};
